import React, { FunctionComponent, useMemo } from 'react';
import { Grid } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';

import DInfoContainer from './DInfoContainer';
import DOperations from './DOperations';
import CustomSwaggerStylesContainer from './CustomSwaggerStylesContainer';

type MissingComponentsWarningOptions = {
  failSilently: boolean;
};

type LayoutProps = {
  // ref: https://swagger.io/docs/open-source-tools/swagger-ui/customization/overview/#getcomponent
  getComponent: (
    name: string,
    exists?: boolean,
    missingComponentsWarningOptions?: MissingComponentsWarningOptions,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => FunctionComponent<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errSelectors: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  specSelectors: any;
};

// Create the layout component.
// Use prebuilt api presets
//   ref: https://github.com/swagger-api/swagger-ui/blob/master/src/core/presets/apis/index.js
// build based on BaseLayout (default Swagger Docs layout)
//   ref: https://github.com/swagger-api/swagger-ui/blob/4d9ec4fb0eaaddb7e1a8f4502e8e8577d40e46ec/src/core/components/layouts/base.jsx#L130
const LegacyReferenceLayout: FunctionComponent<LayoutProps> = ({
  getComponent,
  errSelectors,
  specSelectors,
  ...otherComponentProps
}) => {
  const { translate } = useTranslation();
  const VersionPragmaFilter = getComponent('VersionPragmaFilter');
  const Models = getComponent('Models', true);
  const Errors = getComponent('errors', true);
  const FilterContainer = getComponent('FilterContainer', true);
  const isSwagger2 = specSelectors.isSwagger2();
  const isOAS3 = specSelectors.isOAS3();
  let showModels = false;
  if (isSwagger2 === true) {
    const definitions = specSelectors.definitions();
    showModels = definitions && definitions.size > 0;
  }

  const translations = useMemo(
    () => ({
      'Label.Scheme': translate('Label.Scheme'),
    }),
    [translate],
  );
  const componentProps = useMemo(
    () => ({
      specSelectors,
      errSelectors,
      getComponent,
      ...otherComponentProps,
      translations,
    }),
    [translations, getComponent, specSelectors, errSelectors, otherComponentProps],
  );

  return (
    <VersionPragmaFilter isSwagger2={isSwagger2} isOAS3={isOAS3} alsoShow={<Errors />}>
      <Grid container>
        <Grid item XSmall={12}>
          <DInfoContainer {...componentProps} />
        </Grid>
        <Grid item XSmall={12} marginTop={2}>
          <DOperations {...componentProps} />
        </Grid>
        <Grid item XSmall={12}>
          <FilterContainer />
        </Grid>
        {showModels && (
          <Grid item XSmall={12}>
            <CustomSwaggerStylesContainer>
              <Models />
            </CustomSwaggerStylesContainer>
          </Grid>
        )}
      </Grid>
    </VersionPragmaFilter>
  );
};

export default LegacyReferenceLayout;
